import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  SORT_BY_PARAM,
  CURRENT_PAGE_PARAM,
  CATEGORY_ID_PARAM,
  SEARCH_QUERY_PARAM,
  FILTER_BY_EQUIPMENT_PARAM,
  SERIAL_NUMBER_PARAM,
  MODEL_PARAM,
  FACET_PARAM,
  IS_NER_ENABLED
} from './constants/common';
import { decodeString } from '@cat-ecom/common/utils/stringUtils';
import { SearchParams } from 'app/[lang]/[esite]/(core)/search/types';
import { SearchParams as CategorySearchParams } from 'app/[lang]/[esite]/(core)/category/[category]/types';
import queryString from 'query-string';

export default function useFilter() {
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const options = { scroll: false };

  const handleSortBy = (order: string) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(SORT_BY_PARAM, order);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleResetFilters = (params: SearchParams | CategorySearchParams) => {
    const stringifiedParams = queryString.stringify(params, {
      encode: false,
      sort: false
    });
    const newParams = new URLSearchParams(stringifiedParams);
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleCategoryPLP = (categoryId: string) => {
    const subcategory = decodeString(categoryId);
    const newParams = new URLSearchParams(params.toString());
    const newURL = `${pathname.split('/', 4).join('/')}/${subcategory.toLowerCase()}?${newParams}`;
    router.push(newURL);
  };

  const handleSortByCategory = (categoryId: string) => {
    const id = decodeString(categoryId);
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CATEGORY_ID_PARAM, id);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    newParams.delete(FACET_PARAM);
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleSearchSuggestion = (query: string) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(SEARCH_QUERY_PARAM, query);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleSortByEquipment = (
    checked: boolean,
    model?: string,
    serialNumber?: string,
    isSearchPage?: boolean
  ) => {
    const newParams = new URLSearchParams(params.toString());
    if (checked) {
      newParams.set(FILTER_BY_EQUIPMENT_PARAM, checked.toString());
      newParams.set(MODEL_PARAM, model);

      if (serialNumber) {
        newParams.set(SERIAL_NUMBER_PARAM, serialNumber);
      } else {
        newParams.delete(SERIAL_NUMBER_PARAM);
      }
      if (isSearchPage) {
        newParams.set(IS_NER_ENABLED, 'false');
      }
    } else {
      newParams.delete(FILTER_BY_EQUIPMENT_PARAM);
      newParams.delete(MODEL_PARAM);
      newParams.delete(SERIAL_NUMBER_PARAM);
      newParams.delete(IS_NER_ENABLED);
    }
    newParams.set(CURRENT_PAGE_PARAM, '1');

    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleSortByFacet = (facetId: string, checked: boolean) => {
    const id = decodeString(facetId);
    const newParams = new URLSearchParams(params.toString());

    if (checked) {
      newParams.append(FACET_PARAM, id);
    } else {
      newParams.delete(FACET_PARAM, id);
    }
    newParams.set(CURRENT_PAGE_PARAM, '1');

    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL, options);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CURRENT_PAGE_PARAM, String(page));
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleClearAllFilters = (facetId: string | string[]) => {
    const newParams = new URLSearchParams(params.toString());
    let id: string;

    if (typeof facetId !== 'string') {
      facetId.map((facet: string) => {
        id = decodeString(facet);
        newParams.delete(FACET_PARAM, id);
      });
    } else {
      id = decodeString(facetId);
      newParams.delete(FACET_PARAM, id);
    }
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleSortByUomChange = (
    facetsToDelete: { uom: string; facet: string }[]
  ) => {
    let id: string;
    const newParams = new URLSearchParams(params.toString());

    facetsToDelete.map((facet) => {
      id = decodeString(facet.facet);
      newParams.delete(FACET_PARAM, id);
    });

    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };
  const handleSortByNER = (isNEREnabled: boolean) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(IS_NER_ENABLED, isNEREnabled.toString());
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  return {
    handleSortBy,
    handleCategoryPLP,
    handleClearAllFilters,
    handleSortByCategory,
    handleSortByEquipment,
    handleSearchSuggestion,
    handleSortByFacet,
    handlePageChange,
    handleSortByUomChange,
    handleResetFilters,
    handleSortByNER
  };
}
